/**
 * Audio constants — render-side audio tuning.
 *
 * types.ts and tuning.ts are Kimi-owned and must not be modified. Any audio
 * feel constant introduced for Stage 6 lives here, following the juice.ts
 * precedent. All values are render-side only; they never affect sim behavior.
 *
 * The audio assets live at /assets/audio/*.wav (served statically by Vite
 * from the project root `assets/` directory — same scheme as the GLB models).
 */
export const AUDIO = {
  // --- File paths (root-relative; served from the project `assets/` dir) ---
  paths: {
    // 62.4936 s bed lifted directly from the owner's reference clip: a
    // measured 2-bar loop (172248 frames @ 44.1 kHz = 3.90585 s, ~122.9 BPM)
    // tiled 16x. No generated material — see assets/provenance.md. The file
    // length is an exact integer number of loops and the loop wrap is
    // crossfaded, so `src.loop = true` over the whole buffer is seamless;
    // do NOT add head/tail fades, they punch a hole in that wrap.
    // Retired beds: /assets/audio/retired/music_loop.wav (30 s, prompt-only)
    // and /assets/audio/retired/music_loop_seedaudio_61s.mp3 (seed_audio).
    music: "/assets/audio/music_loop.mp3",
    wind: "/assets/audio/wind.wav",
    engineWhoosh: "/assets/audio/engine_whoosh.wav",
    kick: "/assets/audio/kick_impact.wav",
    crash: "/assets/audio/crash.wav",
    coin: "/assets/audio/coin_pickup.wav",
  },

  // --- Music bed ------------------------------------------------------------
  music: {
    gain: 0.5, // nominal music volume
    duckGain: 0.15, // crash phase duck level
  },

  // --- Wind ambience (rate + gain scaled by forwardSpeed/speedMax) ----------
  wind: {
    rateMin: 0.7, // playbackRate at standstill
    rateMax: 1.4, // playbackRate at speedMax
    gainMin: 0.05, // at standstill
    gainMax: 0.5, // at speedMax
  },

  // --- Engine whoosh (one-shot per threat vehicle pass) ---------------------
  whoosh: {
    maxConcurrent: 3, // hard cap on simultaneous whoosh sources
    skipThreshold: 2, // skip if more than this many already playing
    baseGain: 0.6, // peak gain before the 1/min(|laneGap|,3) scaling
  },

  // --- Kick impact envelope (file is crunch-sustain; needs shaping) ---------
  // Fast attack then decay to 30% by 250 ms per ASSET_PIPELINE note.
  kick: {
    attackMs: 5, // gain ramp to peak
    decayTargetFrac: 0.3, // decay to this fraction of peak
    decayMs: 250, // time to reach decayTargetFrac
    whiffGain: 0.3, // kickWhiff volume
    whiffRate: 1.3, // kickWhiff playbackRate
  },

  // --- Coin pickup chime (polyphonic, rising pitch per chain step) ----------
  // The sample is a dry 0.28 s ding. Every pickup gets its OWN BufferSource
  // (WebAudio one-shot pattern) so a 6-10 coin chain overlaps instead of
  // retriggering one voice. Consecutive pickups climb a semitone ladder —
  // the classic Subway Surfers / Mario coin-run feel — and the ladder resets
  // after `chainResetSec` without a pickup.
  coin: {
    gain: 0.35, // peak gain (modest vs kick 1.0 / whoosh 0.6)
    semitonesPerStep: 2, // +2 semitones per consecutive coin
    maxStep: 8, // ladder caps at +16 semitones (~2.52x rate)
    chainResetSec: 0.8, // gap that resets the ladder to step 0
    maxVoices: 12, // hard cap on simultaneous coin voices
    releaseSec: 0.02, // tail fade so a stolen/ending voice can't click
  },
} as const;
